import { useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { http } from '@/http/http';
import { Endpoints } from '@/http/endpoints';
import { getCurrentUserType, subscribeUserType, type UserType } from '@/auth/user-type';

const USER_AVATAR_KEY = 'userAvatar';
const USER_AVATAR_OWNER_KEY = 'userAvatarEmail';
const BASE64_IMAGE_RE = /^[A-Za-z0-9+/]+={0,2}$/;

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function avatarStorageKey(email: string, userType: UserType): string {
  return `${USER_AVATAR_KEY}:${normalizeEmail(email)}:${userType}`;
}

export function avatarQueryKey(email: string | undefined, userType: UserType) {
  return ['user-avatar', email ? normalizeEmail(email) : null, userType] as const;
}

function readStoredAvatar(email: string, userType: UserType): string {
  const normalizedEmail = normalizeEmail(email);
  for (const storage of [sessionStorage, localStorage]) {
    try {
      const scoped = storage.getItem(avatarStorageKey(normalizedEmail, userType));
      const normalizedScoped = normalizeAvatarSource(scoped);
      if (normalizedScoped) return normalizedScoped;

      if (userType === 'external') continue;
      if (storage.getItem(USER_AVATAR_OWNER_KEY) !== normalizedEmail) continue;
      const stored = storage.getItem(USER_AVATAR_KEY);
      const normalized = normalizeAvatarSource(stored);
      if (normalized) return normalized;
    } catch {
      /* storage unavailable */
    }
  }
  return '';
}

function writeStoredAvatar(email: string, userType: UserType, src: string): void {
  const normalizedEmail = normalizeEmail(email);
  for (const storage of [sessionStorage, localStorage]) {
    try {
      storage.setItem(avatarStorageKey(normalizedEmail, userType), src);
      storage.setItem(USER_AVATAR_OWNER_KEY, normalizedEmail);
      storage.setItem(USER_AVATAR_KEY, src);
    } catch {
      /* storage unavailable */
    }
  }
}

export function normalizeAvatarSource(value: unknown): string {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (/^(data:image\/|https?:\/\/|blob:)/i.test(trimmed)) return trimmed;
    if (trimmed.length > 32 && BASE64_IMAGE_RE.test(trimmed)) {
      return `data:image/jpeg;base64,${trimmed}`;
    }
    return '';
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    for (const key of ['userAvatar', 'avatar', 'photo', 'image', 'data', 'result', 'value']) {
      const normalized = normalizeAvatarSource(record[key]);
      if (normalized) return normalized;
    }
  }

  return '';
}

export function useUserAvatar(email: string | undefined) {
  const userType = useSyncExternalStore(subscribeUserType, getCurrentUserType, getCurrentUserType);

  return useQuery({
    queryKey: avatarQueryKey(email, userType),
    enabled: Boolean(email),
    staleTime: 60 * 60_000,
    queryFn: async () => {
      const normalizedEmail = normalizeEmail(email as string);
      const stored = readStoredAvatar(normalizedEmail, userType);
      if (stored) return stored;

      // TODO: internal external user retrival using session/local storage
      const response = await http<unknown>(Endpoints.adAvatar(userType === 'internal'), {
        method: 'PUT',
        json: normalizedEmail,
      });
      const src = normalizeAvatarSource(response);
      if (src) writeStoredAvatar(normalizedEmail, userType, src);
      return src;
    },
  });
}
